import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/api";
import { BulkEmailTemplate } from "../../types";

interface TemplatesState {
  templates: BulkEmailTemplate[];
  loading: boolean;
  error: string | null;
}

const initialState: TemplatesState = {
  templates: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchTemplates = createAsyncThunk(
  "templates/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      console.log("🔄 STEP 1.1: fetchTemplates thunk - Starting API call");
      const response = await apiService.getTemplates();
      console.log(
        "🔄 STEP 1.2: fetchTemplates thunk - API response:",
        response
      );
      // The API returns { success: true, data: { templates: [], total: 0, ... } }
      const result = (response as any).data || response;
      console.log(
        "🔄 STEP 1.3: fetchTemplates thunk - Returning result:",
        result
      );
      console.log(
        "🔄 STEP 1.3: Templates count:",
        result.templates?.length || 0
      );
      return result;
    } catch (error: any) {
      console.error("❌ STEP 1.4: fetchTemplates thunk - Error:", error);
      return rejectWithValue(error.message || "Failed to fetch templates");
    }
  }
);

export const createTemplate = createAsyncThunk(
  "templates/createTemplate",
  async (templateData: Partial<BulkEmailTemplate>, { rejectWithValue }) => {
    try {
      const response = await apiService.createTemplate(templateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create template");
    }
  }
);

export const updateTemplate = createAsyncThunk(
  "templates/updateTemplate",
  async (
    { id, ...templateData }: { id: string } & Partial<BulkEmailTemplate>,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.updateTemplate(id, templateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update template");
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  "templates/deleteTemplate",
  async (id: string, { rejectWithValue }) => {
    try {
      await apiService.deleteTemplate(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete template");
    }
  }
);

const extractTemplateId = (template: any): string => {
  const rawId =
    template?.id ??
    template?._id ??
    template?.id?.$oid ??
    template?._id?.$oid ??
    template?.uuid ??
    template?._uuid;
  return rawId ? String(rawId) : "";
};

const normalizeTemplate = (template: any) => ({
  ...template,
  id: extractTemplateId(template),
});

const normalizeTemplates = (templates: BulkEmailTemplate[] = []) =>
  templates.map((template) => normalizeTemplate(template));

const templatesSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    clearTemplatesError: (state) => {
      state.error = null;
    },
    clearTemplates: (state) => {
      state.templates = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        console.log("fetchTemplates.pending - Setting loading to true");
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = false;
        console.log(
          "🔄 STEP 1.5: fetchTemplates.fulfilled - action.payload:",
          action.payload
        );

        // Handle different response structures
        if (
          action.payload &&
          action.payload.data &&
          action.payload.data.templates
        ) {
          console.log("Using action.payload.data.templates");
          state.templates = normalizeTemplates(
            action.payload.data.templates as BulkEmailTemplate[]
          );
        } else if (action.payload && action.payload.templates) {
          console.log("Using action.payload.templates");
          state.templates = normalizeTemplates(
            action.payload.templates as BulkEmailTemplate[]
          );
        } else if (Array.isArray(action.payload)) {
          console.log("Using action.payload as array");
          state.templates = normalizeTemplates(action.payload);
        } else {
          console.log("No templates found, setting empty array");
          state.templates = [];
        }

        console.log("Final state.templates:", state.templates);
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        console.log("fetchTemplates.rejected - Error:", action.payload);
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response structures
        const template = action.payload.data || action.payload;
        if (template) {
          state.templates.push(normalizeTemplate(template));
        }
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update template
    builder
      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response structures
        const template = action.payload.data || action.payload;
        if (template) {
          const normalizedTemplate = normalizeTemplate(template);
          const index = state.templates.findIndex(
            (t) =>
              t.id === normalizedTemplate.id ||
              (t as any)._id === normalizedTemplate.id
          );
          if (index !== -1) {
            state.templates[index] = normalizedTemplate;
          }
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete template
    builder
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.filter(
          (template) =>
            template.id !== action.payload &&
            (template as any)._id !== action.payload
        );
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTemplatesError, clearTemplates } = templatesSlice.actions;
export default templatesSlice.reducer;
